import React, { useMemo, useCallback } from 'react'
import { useDispatch } from 'react-redux'

import toLower from 'lodash/toLower'

import { useDialog } from '@galo-monitor/dialog'
import { useT } from '@galo-monitor/i18n'
import { circle } from '@galo-monitor/icons'
import useGaloMonitorApi from '@galo-monitor/services/hooks/useGaloMonitorApi'
import { useSnackbar } from '@galo-monitor/snackbar'
import { BrandActions } from '@galo-monitor/stores/brand'
import DataTable from '@galo-monitor/web-components/DataTable'
import Icon from '@galo-monitor/web-components/Icon'
import ConfirmationModal from '@galo-monitor/web-containers/modals/ConfirmationModal'
import ErrorModal from '@galo-monitor/web-containers/modals/ErrorModal'
import BrandModal from '@galo-monitor/web-containers/modals/root/BrandModal'
import { useRoutes } from '@galo-monitor/web-containers/routes/authenticated'

const Brands = () => {
  const t = useT()
  const snackbar = useSnackbar()
  const { createDialog } = useDialog()
  const dispatch = useCallback(useDispatch(), [])
  const { routes, getActionsBySlug } = useRoutes()

  const { data: brands, mutate, isValidating } = useGaloMonitorApi('/admin/machines/makers')

  const userActions = useMemo(
    () => getActionsBySlug(routes.brandsModels.slug),
    [getActionsBySlug, routes.brandsModels.slug]
  )

  const columns = useMemo(
    () => [
      {
        title: t('name'),
        field: 'name'
      },
      {
        title: t('color'),
        field: 'color',
        render: response => (
          <Icon
            icon={ circle }
            color={ response.color }
            style={ { justifyContent: 'start' } }
          />
        )
      }
    ],
    [t]
  )

  const onAddClick = useCallback(
    () => {
      createDialog({
        id: 'create-brand',
        Component: BrandModal,
        props: {
          onSuccess: mutate
        }
      })
    },
    [createDialog, mutate]
  )

  const onEditClick = useCallback(
    (_, brand) => {
      createDialog({
        id: 'edit-brand',
        Component: BrandModal,
        props: {
          brand,
          onSuccess: mutate
        }
      })
    },
    [createDialog, mutate]
  )

  const onDeleteClick = useCallback(
    (_, brand) => {
      createDialog({
        id: 'confirmation',
        Component: ConfirmationModal,
        props: {
          title: t('are you sure you want to delete the "{name}" {this}?', {
            gender: 'female',
            howMany: 1,
            this: t('brand', { howMany: 1 }),
            name: brand.name
          }),
          onConfirm: () => {
            dispatch(BrandActions.removeBrand(
              brand,
              () => {
                snackbar.success(t('{this} {action} successfully!', {
                  this: t('brand', { howMany: 1 }),
                  action: toLower(t('removed', { gender: 'female', howMany: 1 }))
                }))
                mutate()
              },
              err => {
                createDialog({
                  id: 'brands-error',
                  Component: ErrorModal,
                  props: {
                    title: `${ t('could not {action} the {this}', {
                      howMany: 1,
                      gender: 'female',
                      action: toLower(t('remove')),
                      this: toLower(t('brand', { howMany: 1 }))
                    })  }.`,
                    message: t(err?.response?.data?.message || 'unexpected error occurred')
                  }
                })
              }
            ))
          }
        }
      })
    },
    [createDialog, dispatch, mutate, snackbar, t]
  )

  return (
    <DataTable
      loading={ isValidating }
      data={ brands || [] }
      columns={ columns }
      onAddClick={ userActions.write && onAddClick }
      onEditClick={ userActions.edit && onEditClick }
      onDeleteClick={ userActions.delete && onDeleteClick }
    />
  )
}

export default Brands
